// src/components/categories/CategoryForm.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Switch,
  FormControlLabel,
  Typography,
  Paper,
  Grid,
  Divider,
  Alert,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  AutoFixHigh as AutoFixHighIcon,
  Close as CloseIcon,
  ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material';
import BookingSettingsStep from '../listing/BookingSettingsStep';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { motion } from 'framer-motion';
import { useCategories } from '../../context/CategoryContext';
import { usePermission } from '../../hooks/usePermission';
import { useAuth } from '../../context/AuthContext';
import PERMISSIONS from '../../config/permissionsList'; // Ajustez le chemin si nécessaire
import { useTranslation } from 'react-i18next';
import LoadingOverlay from '../common/LoadingOverlay';

// URL de l'API (à remplacer par votre URL réelle)
const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api/v1';

const OFFER_TYPE_OPTIONS = [
  { value: 'equipment',  labelKey: 'categoryManagement.offerType.options.equipment',  label: 'Équipement' },
  { value: 'service',    labelKey: 'categoryManagement.offerType.options.service',    label: 'Service' },
  { value: 'ride_share', labelKey: 'categoryManagement.offerType.options.ride_share', label: 'Covoiturage' },
  { value: 'property',   labelKey: 'categoryManagement.offerType.options.property',   label: 'Propriété / Hébergement' },
  { value: 'vehicle',    labelKey: 'categoryManagement.offerType.options.vehicle',    label: 'Véhicule' },
  { value: 'product',    labelKey: 'categoryManagement.offerType.options.product',    label: 'Produit' }
];

/**
 * Parse une erreur serveur et la mappe vers des champs sp\u00e9cifiques ou un message global.
 * Retourne { fieldErrors: { [field]: message }, globalMessage: string|null }
 */
const parseServerError = (err) => {
  const status = err.response?.status;
  const data = err.response?.data;
  const message = data?.message || data?.msg || err.message || '';
  const result = { fieldErrors: {}, globalMessage: null };

  // 1. express-validator : tableau d'erreurs structur\u00e9es
  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    for (const ve of data.errors) {
      const field = ve.path || ve.param;
      if (field && !result.fieldErrors[field]) {
        result.fieldErrors[field] = ve.msg || ve.message || message;
      }
    }
    if (Object.keys(result.fieldErrors).length > 0) return result;
  }

  // 2. Conflit 409 = toujours li\u00e9 au nom / slug
  if (status === 409) {
    result.fieldErrors.name = message;
    return result;
  }

  // 3. Erreur 400 : d\u00e9tection par mots-cl\u00e9s multilingues
  if (status === 400) {
    const lower = message.toLowerCase();
    // Nom / Name
    if (/\bnom\b|name/.test(lower)) {
      result.fieldErrors.name = message;
      return result;
    }
    // Type d'offre / Offer type
    if (/offer.?type|type\s*d[''\u2019]offre/.test(lower)) {
      result.fieldErrors.offer_type = message;
      return result;
    }
    // Contexte
    if (/\bcontexte?\b/.test(lower)) {
      result.fieldErrors.context = message;
      return result;
    }
    // Cat\u00e9gorie parente
    if (/parent/.test(lower)) {
      result.fieldErrors.parentId = message;
      return result;
    }
  }

  // 4. Fallback : message global
  result.globalMessage = message;
  return result;
};

const CategoryForm = ({
  category,
  onSubmit,
  onCancel,
  isOwner = false,
  submitButtonLabel = null,
  showRejectAction = false,
  onReject = null,
  rejectButtonLabel = 'Rejeter'
}) => {
  const { categoriesList } = useCategories();
  const { user } = useAuth();
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  // ── Preset de réservation ──────────────────────────────────────────────────
  // presetFormData contient booking_mode + stock_quantity + capacity_default (comme dans CreateListingForm)
  // presetSettings  contient la configuration complète du BookingSettingsStep
  const buildInit = (preset) => ({
    booking_mode:   preset?.booking_mode   || null,
    stock_quantity: preset?.stock_quantity  ?? 1,
    capacity_default: preset?.capacity_default ?? '',
  });
  const [presetFormData,    setPresetFormData]    = useState(() => buildInit(category?.booking_settings_preset));
  const [presetSettings,    setPresetSettings]    = useState(() => category?.booking_settings_preset || {});
  const [presetLabel,       setPresetLabel]       = useState(() => {
    const val = category?.preset_label;
    if (val === null || val === undefined || val === 'null') return '';
    return String(val).trim();
  });
  const [presetLabelError,  setPresetLabelError]  = useState('');
  const [presetEnabled,     setPresetEnabled]     = useState(!!category?.booking_settings_preset?.booking_mode);

  // Re-sync preset state chaque fois que la prop `category` change
  // (ex : dialogue fermé puis rouvert pour la même catégorie après rafraîchissement)
  useEffect(() => {
    const preset = category?.booking_settings_preset;
    setPresetFormData({
      booking_mode: preset?.booking_mode || null,
      stock_quantity: preset?.stock_quantity ?? 1,
      capacity_default: preset?.capacity_default ?? '',
    });
    setPresetSettings(preset || {});
    const labelVal = category?.preset_label;
    if (labelVal === null || labelVal === undefined || labelVal === 'null') {
      setPresetLabel('');
    } else {
      setPresetLabel(String(labelVal).trim());
    }
    setPresetLabelError('');
    setPresetEnabled(!!preset?.booking_mode);
  }, [category]);

  const { t } = useTranslation();
  const isEditMode = !!category?.id;

  // Schéma de validation complet avec messages i18n
  const validationSchema = useMemo(() => {
    const shape = {
      name: Yup.string()
        .required(t('categoryManagement.validation.nameRequired'))
        .min(2, t('categoryManagement.validation.nameTooShort', { min: 2 }))
        .max(50, t('categoryManagement.validation.nameTooLong', { max: 50 })),
      description: Yup.string()
        .max(500, t('categoryManagement.validation.descriptionTooLong', { max: 500 })),
      displayOrder: Yup.number()
        .integer(t('categoryManagement.validation.displayOrderInteger'))
        .min(0, t('categoryManagement.validation.displayOrderPositive')),
      parentId: Yup.string().nullable(),
      context: Yup.string()
        .oneOf(['rental', 'product', 'both'])
        .required(t('categoryManagement.validation.contextRequired')),
      offer_type: Yup.string()
        .oneOf(
          ['equipment', 'service', 'ride_share', 'property', 'vehicle', 'product'],
          t('categoryManagement.offerType.validation.invalid')
        )
        .required(t('categoryManagement.offerType.validation.required')),
    };
    if (isOwner && !isEditMode) {
      shape.reason = Yup.string()
        .required(t('categoryManagement.validation.reasonRequired'))
        .min(10, t('categoryManagement.validation.reasonTooShort', { min: 10 }));
    }
    return Yup.object(shape);
  }, [t, isOwner, isEditMode]);

  // Ref vers le schema courant pour éviter les stale closures dans validate
  const validationSchemaRef = useRef(validationSchema);
  validationSchemaRef.current = validationSchema;

  // Fonction de validation compatible Yup v1 + Formik v2
  // (Formik v2 a un bug avec Yup v1 : yupToFormErrors ne lit pas correctement .inner)
  const validate = useCallback(async (values) => {
    try {
      await validationSchemaRef.current.validate(values, { abortEarly: false });
      return {};
    } catch (err) {
      if (!err || err.name !== 'ValidationError') throw err;
      const errors = {};
      if (err.inner && err.inner.length > 0) {
        for (const e of err.inner) {
          if (e.path && !errors[e.path]) errors[e.path] = e.message;
        }
      } else if (err.path) {
        errors[err.path] = err.message;
      }
      return errors;
    }
  }, []);

  // Vérifier les permissions
  const { hasPermission: canCreateCategory } = usePermission(PERMISSIONS.CATEGORY_CREATE);
  const { hasPermission: canUpdateCategory } = usePermission(PERMISSIONS.CATEGORY_UPDATE);

  // Vérifier si l'utilisateur a la permission appropriée
  const hasPermission = isEditMode ? canUpdateCategory : canCreateCategory;
  
  // Si l'utilisateur n'est pas propriétaire et n'a pas la permission, vérifier s'il peut quand même modifier sa propre catégorie
  const canEditOwnCategory = isEditMode && isOwner && category?.createdBy === user?.id;
  const hasAccess = hasPermission || canEditOwnCategory;

  // Initialiser le formulaire avec Formik
  const formik = useFormik({
    initialValues: {
      name: category?.name || '',
      description: category?.description || '',
      displayOrder: category?.displayOrder || 0,
      parentId: category?.parentId || '',
      context: category?.context || 'both',
      offer_type: category?.offer_type || '',
      isActive: category?.isActive !== undefined ? category.isActive : !isOwner, // Par défaut inactif pour les propriétaires
      reason: '' // Raison de la proposition (uniquement pour les propriétaires)
    },
    validate,
    onSubmit: async (values, { setFieldError: setServerFieldError }) => {
      if (isOwner && !isEditMode) setSending(true);
      try {
        setError(null);
        setPresetLabelError('');
        console.log('Submitting form with values:', values);
        
        // Préparer les données à soumettre
        const dataToSubmit = { ...values };

        // Inclure le preset de réservation s'il est défini
        if (presetEnabled && presetFormData.booking_mode) {
          // Valider strictement: le label du preset ne peut pas être vide, null, ou la string "null"
          const trimmedLabel = String(presetLabel || '').trim();
          if (!trimmedLabel || trimmedLabel === 'null') {
            setPresetLabelError(t('categoryManagement.validation.presetLabelRequired'));
            setError(null);
            if (isOwner && !isEditMode) setSending(false);
            return;
          }
          dataToSubmit.booking_settings_preset = {
            ...presetSettings,
            booking_mode:   presetFormData.booking_mode,
            stock_quantity: presetFormData.stock_quantity ?? 1,
            capacity_default: presetFormData.capacity_default ?? '',
          };
          dataToSubmit.preset_label = trimmedLabel;
        } else {
          dataToSubmit.booking_settings_preset = null;
          dataToSubmit.preset_label = null;
        }
        
        // Pour les propriétaires, ajouter les champs spécifiques
        if (isOwner && !isEditMode) {
          dataToSubmit.pendingApproval = true;
          dataToSubmit.isActive = false;
        }
        
        // Si nous avons un fichier image, créer un FormData
        if (imageFile) {
          console.log('Submitting with image file:', imageFile);
          const formData = new FormData();
          
          // Ajouter toutes les valeurs du formulaire au FormData
          Object.keys(dataToSubmit).forEach(key => {
            if (key !== 'reason' || (isOwner && key === 'reason')) {
              const value = dataToSubmit[key];
              if (key === 'booking_settings_preset' && value !== undefined) {
                formData.append(key, value === null ? 'null' : JSON.stringify(value));
              } else {
                formData.append(key, value);
              }
            }
          });
          
          // Ajouter le fichier image - utiliser uniquement le nom correct
          formData.append('categoryImage', imageFile); // Seul ce nom est attendu par le backend
          
          // Log pour déboguer
          for (let pair of formData.entries()) {
            console.log(pair[0] + ': ' + pair[1]);
          }
          
          await onSubmit(formData, true);
        } else {
          // Si nous sommes en mode édition et qu'il y a une image existante
          if (isEditMode && category?.imageUrl && !imagePreview) {
            // L'image a été supprimée
            dataToSubmit.removeImage = true;
          }
          
          // Sinon, soumettre les valeurs normalement
          console.log('Submitting without image:', dataToSubmit);
          await onSubmit(dataToSubmit, false);
        }
      } catch (err) {
        console.error('Error submitting form:', err);
        // Parser l'erreur serveur et mapper vers les champs ou message global
        const { fieldErrors, globalMessage } = parseServerError(err);
        // Afficher les erreurs sur les champs concern\u00e9s
        Object.entries(fieldErrors).forEach(([field, msg]) => {
          setServerFieldError(field, msg);
        });
        // Message global si l'erreur n'est pas li\u00e9e \u00e0 un champ sp\u00e9cifique
        if (globalMessage) {
          setError(globalMessage);
        } else if (Object.keys(fieldErrors).length > 0) {
          setError(t('categoryManagement.validation.errorSummary'));
        } else {
          setError(t('categoryManagement.validation.submitError'));
        }
      } finally {
        if (isOwner && !isEditMode) setSending(false);
      }
    }
  });

  // Mettre à jour l'aperçu de l'image lorsque la catégorie change
  useEffect(() => {
    if (category?.imageUrl) {
      // Construire l'URL complète de l'image
      const imageUrl = category.imageUrl.startsWith('http') 
        ? category.imageUrl 
        : `${API_URL}/${category.imageUrl}`;
      
      console.log('Setting image preview from category:', imageUrl);
      setImagePreview(imageUrl);
    } else {
      setImagePreview(null);
    }
  }, [category]);

  // Scroll vers le premier champ en erreur après une tentative de soumission
  useEffect(() => {
    if (formik.submitCount === 0) return;
    const fieldPriority = ['name', 'description', 'reason', 'offer_type', 'context'];
    const firstErrorField = fieldPriority.find(field => formik.errors[field]);
    if (!firstErrorField) return;
    const el = document.getElementById(firstErrorField);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (typeof el.focus === 'function') el.focus({ preventScroll: true });
    }
  }, [formik.submitCount, formik.errors]);

  // Helpers : afficher l'erreur dès la soumission OU après que le champ ait été touché
  const showErr = (field) =>
    (formik.submitCount > 0 || !!formik.touched[field]) && !!formik.errors[field];
  const getErr = (field) =>
    showErr(field) ? formik.errors[field] : null;

  // Gérer le changement d'image
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      console.log('Image file selected:', file);
      setImageFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        console.log('Image preview created');
        setImagePreview(reader.result);
      };
      reader.onerror = (error) => {
        console.error('Error reading file:', error);
        setError(t('categoryManagement.validation.imageReadError'));
      };
      reader.readAsDataURL(file);
    }
  };

  // Supprimer l'image
  const handleRemoveImage = () => {
    console.log('Removing image');
    setImagePreview(null);
    setImageFile(null);
  };
  // Si l'utilisateur n'a pas la permission, afficher un message d'erreur
  if (!hasAccess) {
    return (
      <Paper 
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        elevation={3} 
        sx={{ p: 3, borderRadius: 2 }}
      >
        <Typography variant="h5" color="error" gutterBottom>
          {t('common.accessDenided')}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Typography variant="body1" paragraph>
          {t(isEditMode ? 'categoryManagement.noPermissionToModifyCategory' : 'categoryManagement.noPermissionToCreateCategory')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={onCancel}
          startIcon={<CancelIcon />}
        >
          {t('categoryManagement.back')}
        </Button>
      </Paper>
    );
  }

  return (
    <Paper 
  component={motion.div}
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.3 }}
  elevation={3} 
  sx={{ p: 3, borderRadius: 2 }}
>
  <LoadingOverlay
    isOpen={sending}
    title={t('listing.overlay.sending', 'Soumission en cours...')}
    subtitle={t('listing.overlay.pleaseWait', 'Veuillez patienter, cette opération peut prendre quelques instants...')}
    warning={t('listing.overlay.doNotClose', 'Ne fermez pas cette page.')}
    primaryColor="#6200ee"
    accentColor="#03dac4"
  />
  <Typography variant="h5" gutterBottom>
    {isEditMode 
      ? t('categoryManagement.editCategory') 
      : isOwner 
        ? t('categoryManagement.proposeCategorySuggest') 
        : t('categoryManagement.newCategoryCreate')
    }
  </Typography>
  <Divider sx={{ mb: 3 }} />
  {error && (
    <Alert
      severity="error"
      icon={<ErrorOutlineIcon fontSize="inherit" />}
      action={
        <IconButton
          aria-label="close"
          color="inherit"
          size="small"
          onClick={() => setError(null)}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
      }
      sx={{
        mb: 3,
        border: '1px solid',
        borderColor: 'error.main',
        '& .MuiAlert-message': { fontWeight: 500 }
      }}
    >
      {error}
    </Alert>
  )}
  {/* Message d'information pour les propriétaires */}
  {isOwner && !isEditMode && (
    <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
      {t('categoryManagement.ownerProposalMessage')}
    </Alert>
  )}
  {isOwner && isEditMode && category?.pendingApproval && (
    <Alert severity="warning" sx={{ mb: 3 }}>
      {t('categoryManagement.pendingApprovalWarning')}
    </Alert>
  )}
  <form onSubmit={formik.handleSubmit} noValidate>
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <TextField
          fullWidth
          id="name"
          name="name"
          label={t('common.name')}
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={showErr('name')}
          helperText={getErr('name')}
          variant="outlined"
          margin="normal"
        />
        <TextField
          fullWidth
          id="description"
          name="description"
          label={t('common.description')}
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={showErr('description')}
          helperText={getErr('description')}
          variant="outlined"
          margin="normal"
          multiline
          rows={4}
        />
        {/* Champ de raison pour les propriétaires */}
        {isOwner && !isEditMode && (
          <TextField
            fullWidth
            id="reason"
            name="reason"
            label={t('categoryManagement.reason')}
            value={formik.values.reason}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={showErr('reason')}
            helperText={getErr('reason')}
            variant="outlined"
            margin="normal"
            multiline
            rows={3}
            placeholder={t('categoryManagement.reasonPlaceholder')}
          />
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              id="displayOrder"
              name="displayOrder"
              label={t('common.orderShow')}
              type="number"
              value={formik.values.displayOrder}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={showErr('displayOrder')}
              helperText={getErr('displayOrder')}
              variant="outlined"
              InputProps={{ inputProps: { min: 0 } }}
              disabled={isOwner} // Désactivé pour les propriétaires
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" error={showErr('offer_type')} required>
              <InputLabel id="offer-type-label">{t('categoryManagement.offerType.label', 'Type d\'offre *')}</InputLabel>
              <Select
                labelId="offer-type-label"
                id="offer_type"
                name="offer_type"
                value={formik.values.offer_type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label={t('categoryManagement.offerType.label', 'Type d\'offre *')}
              >
                {OFFER_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey, opt.label)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText error={showErr('offer_type')}>
                {getErr('offer_type') ||
                  t('categoryManagement.offerType.helper', 'Définit le type d\'annonce associé à cette catégorie. Sera appliqué automatiquement à la création d\'un listing.')}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined" error={showErr('context')}>
              <InputLabel id="category-context-label">{t('categoryManagement.context.label', 'Contexte de la catégorie')}</InputLabel>
              <Select
                labelId="category-context-label"
                id="context"
                name="context"
                value={formik.values.context}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label={t('categoryManagement.context.label', 'Contexte de la catégorie')}
              >
                <MenuItem value="rental">{t('categoryManagement.context.options.rental', 'Listings & services')}</MenuItem>
                <MenuItem value="product">{t('categoryManagement.context.options.product', 'Produits')}</MenuItem>
                <MenuItem value="both">{t('categoryManagement.context.options.both', 'Les deux')}</MenuItem>
              </Select>
              <FormHelperText error={showErr('context')}>
                {getErr('context') || t('categoryManagement.context.helper', 'Définissez si cette catégorie sert aux annonces, aux produits, ou aux deux.')}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="parent-category-label">{t('categoryManagement.parentCategory')}</InputLabel>
              <Select
                labelId="parent-category-label"
                id="parentId"
                name="parentId"
                value={formik.values.parentId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.parentId && Boolean(formik.errors.parentId)}
                label={t('categoryManagement.parentCategory')}
              >
                <MenuItem value="">
                  <em>{t('categoryManagement.rootCategoryNotFound')}</em>
                </MenuItem>
                {categoriesList
                  .filter(cat => cat.id !== category?.id)
                  .filter(cat => {
                    if (!cat.context || cat.context === 'both' || formik.values.context === 'both') {
                      return true;
                    }

                    return cat.context === formik.values.context;
                  })
                  .map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
              </Select>
              {formik.touched.parentId && formik.errors.parentId && (
                <FormHelperText error>{formik.errors.parentId}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
        {/* Switch d'activation uniquement pour les administrateurs */}
        {!isOwner && hasPermission && (
          <FormControlLabel
            control={
              <Switch
                checked={formik.values.isActive}
                onChange={(e) => formik.setFieldValue('isActive', e.target.checked)}
                name="isActive"
                color="primary"
              />
            }
            label={t('categoryManagement.activeCategory')}
            sx={{ mt: 2 }}
          />
        )}
      </Grid>
      <Grid item xs={12} md={4}>
        <Typography variant="subtitle1" gutterBottom>
          {t('categoryManagement.categoryImage')}
        </Typography>
        <Box
          sx={{
            border: '1px dashed',
            borderColor: 'grey.400',
            borderRadius: 1,
            p: 2,
            textAlign: 'center',
            mb: 2,
            height: 200,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {imagePreview ? (
            <>
              <Box
                component="img"
                src={imagePreview}
                alt={t('common.imagePreview')}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  console.error('Error loading image preview:', e);
                  e.target.src = 'https://via.placeholder.com/200x200?text=Image+non+disponible';
                }}
              />
              <IconButton
                size="small"
                onClick={handleRemoveImage}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </>
          ) : (
            <>
              <PhotoCameraIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {t('common.uploadImage')}
              </Typography>
            </>
          )}
          <input
            accept="image/*"
            type="file"
            id="image-upload"
            onChange={handleImageChange}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
              cursor: 'pointer'
            }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {t('common.acceptedImageFormats')}
        </Typography>
      </Grid>
    </Grid>
      {/* ======================================================
          Section : Pré-paramétrage réservation
      ====================================================== */}
      {!isOwner && (
        <Box sx={{ mt: 4 }}>
          <Accordion
            expanded={presetEnabled}
            onChange={(_, expanded) => setPresetEnabled(expanded)}
            sx={{ border: '1px solid', borderColor: presetEnabled ? 'primary.main' : 'divider', borderRadius: 2, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <AutoFixHighIcon color={presetEnabled ? 'primary' : 'action'} />
                <Box>
                  <Typography fontWeight={700}>
                    {t('categoryManagement.preset.title')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('categoryManagement.preset.subtitle')}
                  </Typography>
                </Box>
                {presetEnabled && presetFormData.booking_mode && (
                  <Chip size="small" label={presetFormData.booking_mode} color="primary" sx={{ ml: 1 }} />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>
                {t('categoryManagement.preset.infoNote')}
              </Alert>
              <TextField
                fullWidth
                label={t('categoryManagement.preset.labelField')}
                value={String(presetLabel || '').replace(/^null$/, '')}
                onChange={e => {
                  const val = String(e.target.value || '').trim();
                  setPresetLabel(val);
                  if (val && val !== 'null') setPresetLabelError('');
                }}
                placeholder={t('categoryManagement.preset.labelPlaceholder', { name: formik.values.name || '' })}
                size="small"
                sx={{ mb: 3 }}
                required
                error={!!presetLabelError}
                helperText={presetLabelError || t('categoryManagement.preset.labelHelper')}
              />
              <BookingSettingsStep
                formData={presetFormData}
                setFormData={setPresetFormData}
                bookingSettings={presetSettings}
                setBookingSettings={setPresetSettings}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
      <Button
        variant="outlined"
        color="secondary"
        onClick={onCancel}
        startIcon={<CancelIcon />}
      >
        {t('common.cancel')}
      </Button>
      {showRejectAction && typeof onReject === 'function' && (
        <Button
          type="button"
          variant="contained"
          color="error"
          onClick={onReject}
          disabled={formik.isSubmitting}
          startIcon={<CancelIcon />}
        >
          {rejectButtonLabel}
        </Button>
      )}
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={formik.isSubmitting}
        startIcon={<SaveIcon />}
      >
        {submitButtonLabel || (isEditMode 
          ? t('common.update') 
          : isOwner 
            ? t('categoryManagement.submitForApproval') 
            : t('common.create')
        )}
      </Button>
    </Box>
  </form>
</Paper>
  );
};

export default CategoryForm;